/**
 * Clinic referral codes.
 *
 * Two ways in, one state machine:
 *
 *   QR   — a patient scans a card in a waiting room and lands on `/?c=CODE`.
 *          The code is held until they have a token, then redeemed for them.
 *   Typed — a staff member who never saw a QR enters the code by hand.
 *
 * A staff code restricted to an employer's domains does not grant a seat on
 * first ask: it sends a six-digit code to the work address and comes back
 * asking for it. That second step is why this is a state machine rather than
 * a single call — the UI has to be able to stop half way and prompt.
 *
 * The URL carries the code as a query parameter on the existing landing page
 * rather than a dedicated path, because cards are printed before features
 * ship: `/?c=X` loads today and is ignored, where `/c/X` would 404 on cards
 * already sitting in a waiting room.
 */

const CODE_KEY = 'hcai_referral';
const STATE_KEY = 'hcai_referral_state';

// Attribution window. Long enough to scan on Tuesday and sign up at the
// weekend; short enough that a code cannot follow someone for months and take
// credit for an unrelated signup.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // private mode, blocked storage — never break the app over this
  }
};
const write = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
};
const drop = (key) => {
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
};

/** Codes are printed on cards and typed by people in waiting rooms. */
export const normalise = (raw) => String(raw || '').trim().toUpperCase().replace(/\s+/g, '');

/**
 * Pull `?c=` out of the URL, remember it, and strip it from the address bar so
 * it cannot ride along in history or in a link the patient shares.
 */
export function captureReferralCode() {
  let params;
  try { params = new URLSearchParams(window.location.search); } catch { return null; }

  const raw = params.get('c');
  if (!raw) return null;

  const code = normalise(raw).slice(0, 64);
  if (!code) return null;

  write(CODE_KEY, { code, at: Date.now() });

  params.delete('c');
  const qs = params.toString();
  window.history.replaceState(
    {}, '',
    window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
  );
  return code;
}

/** The code waiting to be redeemed, if there is one and it has not gone stale. */
export function pendingReferralCode() {
  const held = read(CODE_KEY);
  if (!held?.code) return null;
  if (Date.now() - held.at > TTL_MS) { drop(CODE_KEY); return null; }
  return held.code;
}

export const referralState = () => read(STATE_KEY);
export const clearReferralState = () => drop(STATE_KEY);

/** Friendly text for every failure the API can return. */
function messageFor(reason, fallback) {
  switch (reason) {
    case 'INVALID_CODE': return 'We don’t recognise that code. Check it against your card.';
    case 'CODE_EXPIRED': return 'That code has expired. Ask the front desk for a current one.';
    case 'CODE_EXHAUSTED': return 'That code has been fully used.';
    case 'NO_SEATS': return 'Your organisation has no seats left. Ask them to add one.';
    case 'ALREADY_REDEEMED': return 'This account is already linked to an organisation — each account can link only once.';
    case 'DOMAIN_NOT_ELIGIBLE': return 'That code is for staff. Sign in with your work email address.';
    case 'VERIFICATION_INVALID': return 'That code isn’t right. Check the email and try again.';
    case 'VERIFICATION_EXPIRED': return 'That code expired. Request a new one.';
    case 'VERIFICATION_LOCKED': return 'Too many tries. Request a new code.';
    case 'VERIFICATION_UNAVAILABLE': return 'We couldn’t send your code. Try again in a moment.';
    case 'RATE_LIMITED': return 'Too many attempts. Try again later.';
    case 'NOT_SIGNED_IN': return 'Sign in first — then this code will connect automatically.';
    default: return fallback || 'Something went wrong. Please try again.';
  }
}

/**
 * Redeem a code, optionally with the six digits from the verification email.
 *
 * Never throws: attribution is a marketing concern and must not be able to
 * break a sign-in. Returns one of:
 *
 *   { status:'ok',     … }          seat or attribution granted
 *   { status:'verify', code, email } we emailed six digits; ask for them
 *   { status:'error',  reason, message }
 *   null                             nothing to do, or worth retrying later
 */
export async function redeem(api, rawCode, verification, { deliberate = true } = {}) {
  const code = normalise(rawCode);
  if (!code) return null;

  try {
    const res = await api.post('/org/redeem', verification ? { code, verification } : { code });

    // 202 is a SUCCESS status, so it arrives here rather than in the catch.
    // Missing that is how this step silently does nothing.
    if (res.status === 202 || res.data?.code === 'VERIFICATION_REQUIRED') {
      const state = {
        status: 'verify',
        code,
        email: res.data?.email || '',
        expiresInMinutes: res.data?.expiresInMinutes ?? 15,
      };
      write(STATE_KEY, state);
      return state;
    }

    drop(CODE_KEY);
    const state = { status: 'ok', ...res.data };
    write(STATE_KEY, state);
    return state;
  } catch (err) {
    const status = err?.response?.status;
    const reason = err?.response?.data?.code;

    // Checked BEFORE the transient branch, because VERIFICATION_LOCKED is a 429
    // and being locked out of a challenge is a settled answer, not a rate limit
    // to wait out. Ordering these the other way swallows it.
    //
    // A wrong or expired six digits leaves the challenge open — the user should
    // be able to correct a typo without starting over.
    if (['VERIFICATION_INVALID', 'VERIFICATION_EXPIRED', 'VERIFICATION_LOCKED'].includes(reason)) {
      const prior = read(STATE_KEY);
      const state = { ...(prior || { status: 'verify', code }), status: 'verify',
                      error: messageFor(reason), canResend: reason !== 'VERIFICATION_INVALID' };
      write(STATE_KEY, state);
      return state;
    }

    // No session. Ordinary rather than exceptional: the token lives in
    // sessionStorage, which is per-tab and cleared eagerly on phone browsers, so
    // anyone arriving from the app's website link lands here. Without this the
    // server's own words reach the user as "Missing or invalid authorization
    // header", which reads like a fault rather than an instruction.
    //
    // The code is kept, so signing in redeems it without it being typed again.
    if (status === 401) {
      return { status: 'error', reason: 'NOT_SIGNED_IN',
               message: messageFor('NOT_SIGNED_IN'), retryable: true };
    }

    // Rate-limited, server error or offline: keep the code and try again on the
    // next sign-in rather than silently losing the attribution.
    if (!status || status === 429 || status >= 500) {
      return { status: 'error', reason: reason || 'RATE_LIMITED', message: messageFor(reason), retryable: true };
    }

    // Anything else is settled — stop retrying this code.
    drop(CODE_KEY);

    // Silence here depends on whether the person asked.
    //
    // On the QR path this is the expected outcome for someone who picks up a
    // second card, and interrupting them over it would be noise.
    //
    // For a typed code it is the opposite: somebody entered a code, pressed a
    // button, and is owed an answer. Staying quiet there made a real staff
    // member believe the verification email had failed, when in fact a patient
    // code left in this browser days earlier had already claimed their one
    // redemption at sign-up.
    if (reason === 'ALREADY_REDEEMED') {
      drop(STATE_KEY);
      if (!deliberate) return null;
      const state = { status: 'error', reason, message: messageFor(reason) };
      write(STATE_KEY, state);
      return state;
    }

    const state = { status: 'error', reason, message: messageFor(reason, err?.response?.data?.error) };
    write(STATE_KEY, state);
    return state;
  }
}

/** Redeem whatever arrived by QR. Called once a token exists. */
export async function redeemPendingCode(api) {
  const code = pendingReferralCode();
  if (!code) return null;
  // Nobody asked for this one — it is a code carried in from a scan, redeemed
  // the moment a token exists. Failures that need no action stay quiet.
  return redeem(api, code, undefined, { deliberate: false });
}
